import * as React from "react"
import { preload as preloadResource } from "react-dom"

import {
  getImgProps,
  imageConfigDefault,
  optimizedUrl,
  type ImageConfig,
  type ImageOptions,
} from "@car/ui/lib/image"

// A drop-in for next/image: same props, same srcset/sizes algorithm, lazy by default,
// blur placeholder, preload for the LCP image. Optimized URLs point at the API's
// `/image` endpoint (WebP at the requested width); set it once per app with
// <ImageConfigProvider value={{ path: `${serverUrl}/image` }}>.

const ImageConfigContext = React.createContext<ImageConfig>(imageConfigDefault)

function ImageConfigProvider({
  value,
  children,
}: {
  value: Partial<ImageConfig>
  children: React.ReactNode
}) {
  const parent = React.useContext(ImageConfigContext)
  const merged = React.useMemo(() => ({ ...parent, ...value }), [parent, value])
  return <ImageConfigContext.Provider value={merged}>{children}</ImageConfigContext.Provider>
}

const useImageConfig = () => React.useContext(ImageConfigContext)

/**
 * `(src, width, quality?) => url` of the optimized image, for code that sets a src
 * itself (a video poster, a lens swapping images); unoptimizable sources pass through
 */
function useImageUrl() {
  const config = useImageConfig()
  return React.useCallback(
    (src: string, width: number, quality?: number) => optimizedUrl(src, width, config, quality),
    [config],
  )
}

type ImageProps = ImageOptions &
  Omit<
    React.ComponentProps<"img">,
    keyof ImageOptions | "srcSet" | "children" | "dangerouslySetInnerHTML"
  >

function Image({
  ref,
  onLoad,
  onError,
  src,
  alt,
  width,
  height,
  fill,
  sizes,
  quality,
  preload,
  priority,
  loading,
  fetchPriority,
  decoding,
  placeholder,
  blurDataURL,
  unoptimized,
  loader,
  overrideSrc,
  className,
  style,
  ...imgRest
}: ImageProps) {
  const config = useImageConfig()
  const [blurComplete, setBlurComplete] = React.useState(false)
  const [showAltText, setShowAltText] = React.useState(false)
  const { props, meta } = getImgProps(
    {
      src,
      alt,
      width,
      height,
      fill,
      sizes,
      quality,
      preload,
      priority,
      loading,
      fetchPriority,
      decoding,
      placeholder,
      blurDataURL,
      unoptimized,
      loader,
      overrideSrc,
      className,
      style,
    },
    { config, blurComplete, showAltText },
  )

  // the LCP image: <link rel=preload imagesrcset> goes into <head>, during SSR too
  if (meta.preload) {
    preloadResource(props.src, {
      as: "image",
      imageSrcSet: props.srcSet,
      imageSizes: props.sizes,
      fetchPriority: "high",
      crossOrigin: imgRest.crossOrigin,
      referrerPolicy: imgRest.referrerPolicy,
    })
  }

  const handled = React.useRef("")
  // an image the browser had cached can finish before hydration attached onLoad:
  // replay its load event so the placeholder and the caller's onLoad still run
  const replayLoad = React.useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth > 0) img.dispatchEvent(new Event("load"))
  }, [])
  const mergedRef = React.useMemo(() => mergeRefs(ref, replayLoad), [ref, replayLoad])

  return (
    <img
      {...imgRest}
      {...props}
      ref={mergedRef}
      data-nimg={meta.fill ? "fill" : "1"}
      onLoad={(event) => {
        const img = event.currentTarget
        const current = img.currentSrc || img.src
        if (handled.current === current) return
        handled.current = current
        onLoad?.(event)
        if (meta.placeholder !== "empty") {
          // drop the blur only once the pixels can be painted, never on a blank frame
          img
            .decode()
            .catch(() => undefined)
            .then(() => setBlurComplete(true))
        }
      }}
      onError={(event) => {
        setShowAltText(true)
        if (meta.placeholder !== "empty") setBlurComplete(true)
        onError?.(event)
      }}
    />
  )
}

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return (value) => {
    const cleanups: (() => void)[] = []
    for (const ref of refs) {
      if (!ref) continue
      if (typeof ref === "function") {
        const cleanup = ref(value)
        if (cleanup) cleanups.push(cleanup)
      } else {
        ref.current = value
      }
    }
    return cleanups.length > 0 ? () => cleanups.forEach((cleanup) => cleanup()) : undefined
  }
}

export { Image, ImageConfigProvider, useImageConfig, useImageUrl, type ImageProps }
